import React, { useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import {staffDestination} from '@/lib/staffDestination';
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Wordmark, GoldRule } from "@/components/Brand";

export function StaffLogin() {
    const { user, login } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const destination = staffDestination(new URLSearchParams(location.search).get('next'));
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (user) return <Navigate to={destination} replace />;

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await login(email.trim(), password);
            nav(destination, { replace: true });
        } catch (err) {
            toast.error(err?.message || "Login failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-[100dvh] flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md" data-testid="staff-login-page">
                <div className="flex justify-center"><Wordmark /></div>
                <GoldRule className="mt-6" />
                <form onSubmit={submit} className="mt-10 rounded-3xl glass p-8 space-y-6">
                    <div className="text-center">
                        <div className="mx-auto h-14 w-14 rounded-full glass flex items-center justify-center border-gold/50 border">
                            <Lock className="h-5 w-5 text-gold" strokeWidth={1.5} />
                        </div>
                        <h1 className="mt-6 font-serif font-light text-3xl text-white">
                            Staff <span className="gold-text">sign in</span>
                        </h1>
                        <p className="mt-2 text-sm text-white/50">The clinic dashboard for Angela.</p>
                    </div>

                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 mb-2">Email</div>
                        <input
                            data-testid="staff-login-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="username"
                            required
                            className="w-full bg-transparent border-b hairline focus:border-gold outline-none py-3 text-white placeholder-white/25 font-ui text-base transition-colors"
                        />
                    </label>
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 mb-2">Password</div>
                        <input
                            data-testid="staff-login-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                            className="w-full bg-transparent border-b hairline focus:border-gold outline-none py-3 text-white placeholder-white/25 font-ui text-base transition-colors"
                        />
                    </label>

                    <button
                        type="submit"
                        disabled={submitting || !email || !password}
                        data-testid="staff-login-submit"
                        className="w-full h-14 rounded-full bg-gold text-navy font-ui font-semibold text-base tracking-wide shadow-[0_10px_30px_-8px_rgba(197,160,89,0.55)] hover:bg-gold-light active:scale-[0.98] transition-all disabled:opacity-40"
                    >
                        {submitting ? "Signing in..." : "Sign in"}
                    </button>
                    <div className="text-center">
                        <Link to="/staff/forgot-password" data-testid="staff-forgot-link"
                              className="text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-gold transition-colors">
                            Forgot password?
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
